import { ref, computed, readonly } from 'vue';
import { useAuthStore } from '../stores/auth';
import { supabase } from '../lib/supabaseClient';

// ─── Shared reactive state (singleton across all component instances) ─────────
const usageData = ref(null);
const planData = ref(null);
const subscriptionData = ref(null);
const featuresData = ref(null);
const limitsData = ref(null);
const usageLoading = ref(false);
const usageError = ref(null);
const lastFetched = ref(null);

// Cache duration: 5 minutes
const CACHE_MS = 5 * 60 * 1000;

/**
 * Central composable for subscription and usage management.
 * Provides plan info, usage tracking, limit checking, and feature gating.
 */
export function useSubscription() {
  const authStore = useAuthStore();

  // ── Computed plan info ──────────────────────────────────────────────────
  const plan = computed(() => planData.value || { id: 'free', name: 'Free' });
  const usage = computed(() => usageData.value || {});
  const features = computed(() => featuresData.value || {});
  const limits = computed(() => limitsData.value || {});
  const subscription = computed(() => subscriptionData.value || {});
  const isFreeTier = computed(() => plan.value.id === 'free');
  const isPaidTier = computed(() => plan.value.id !== 'free');

  // ── Fetch usage from edge function ──────────────────────────────────────
  async function refreshUsage(force = false) {
    const orgId = authStore.organizationId;
    if (!orgId) return;

    // Use cache unless forced
    if (!force && lastFetched.value && Date.now() - lastFetched.value < CACHE_MS) {
      return;
    }

    try {
      usageLoading.value = true;
      usageError.value = null;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-usage`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ organization_id: orgId }),
        }
      );

      // 401 here means the JWT we just pulled from local storage was rejected
      // by Supabase — typically because the session was revoked server-side
      // (admin action, password change, global signOut from another origin).
      // Self-heal: clear local state and bounce to /login. We use a hard
      // navigation rather than router.push so any other stale composable
      // state in the SPA gets wiped too.
      if (response.status === 401) {
        console.warn('[useSubscription] check-usage returned 401 — session appears revoked; signing out');
        await authStore.logout();
        if (typeof window !== 'undefined') {
          window.location.assign('/login');
        }
        return;
      }

      if (!response.ok) {
        throw new Error(`Usage check failed: ${response.status}`);
      }

      const result = await response.json();

      planData.value = result.plan;
      usageData.value = result.usage;
      featuresData.value = result.features;
      limitsData.value = result.limits;
      subscriptionData.value = result.subscription;
      lastFetched.value = Date.now();
    } catch (err) {
      console.warn('[useSubscription] Error fetching usage:', err);
      usageError.value = err.message;
    } finally {
      usageLoading.value = false;
    }
  }

  // ── Limit checking ──────────────────────────────────────────────────────

  /**
   * Check if the org can create/use more of a resource.
   * For per-seat plans, 'seats' is always allowed (seats are the billing dimension, not a hard limit).
   * @param {'events_created'|'emails_sent'|'sms_sent'|'evaluation_ocr'|'attendees'|'seats'|'offices'|'campaigns'} resource
   * @returns {boolean}
   */
  function canCreate(resource) {
    // For per-seat pricing, seats are always allowed (they adjust billing, not access)
    if (resource === 'seats' && planData.value?.pricing_model === 'per_seat') {
      return true;
    }
    const u = usageData.value?.[resource];
    if (!u) return true; // No data = allow (fail open)
    if (u.limit === -1) return true; // Unlimited
    return u.current < u.limit;
  }

  /**
   * Get the usage percentage for a resource (0-100).
   */
  function usagePercentage(resource) {
    const u = usageData.value?.[resource];
    if (!u) return 0;
    return u.percent || 0;
  }

  /**
   * Get human-readable usage text like "2 / 3" or "45 / 100".
   */
  function usageText(resource) {
    const u = usageData.value?.[resource];
    if (!u) return '';
    if (u.limit === -1) return `${u.current} / Unlimited`;
    return `${u.current} / ${u.limit}`;
  }

  /**
   * Check whether a feature is enabled on the current plan.
   */
  function isFeatureEnabled(feature) {
    return !!featuresData.value?.[feature];
  }

  /**
   * Get the minimum plan that unlocks a feature.
   */
  function getRequiredPlan(feature) {
    const featureToMinPlan = {
      waitlist_auto_promote: 'professional',
      advanced_reporting: 'starter',
      ai_reports: 'professional',
      evaluation_ocr: 'professional',
      planning_periods: 'professional',
      custom_templates: 'professional',
      appointment_booking: 'professional',
      white_label: 'enterprise',
      lead_management_filters: 'enterprise',
      custom_email_domain: 'professional',
      custom_subdomain: 'professional',
    };
    return featureToMinPlan[feature] || 'professional';
  }

  /**
   * Get the minimum plan that provides more of a resource.
   */
  function getUpgradePlanForResource(resource) {
    const current = plan.value.id;
    if (current === 'free') return 'starter';
    if (current === 'starter') return 'professional';
    if (current === 'professional') return 'enterprise';
    return null; // Already on enterprise
  }

  /**
   * Check if any resource is at or above 80% usage (threshold for warning banners).
   */
  function getHighUsageResources() {
    if (!usageData.value) return [];

    const isPerSeatPlan = planData.value?.pricing_model === 'per_seat';

    return Object.entries(usageData.value)
      .filter(([key, data]) => {
        // For per-seat plans, never warn about seats (they're the billing dimension)
        if (key === 'seats' && isPerSeatPlan) return false;
        return data.percent >= 80 && data.limit !== -1 && data.limit > 0;
      })
      .map(([key, data]) => ({
        resource: key,
        ...data,
      }));
  }

  /**
   * Increment a usage counter server-side via the increment-usage edge function.
   * Call this after a successful create action in stores.
   */
  async function incrementUsageCounter(usageKey, amount = 1) {
    const orgId = authStore.organizationId;
    if (!orgId) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/increment-usage`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ organization_id: orgId, usage_key: usageKey, amount }),
        }
      );

      // Invalidate the cache so the next canCreate check reflects the new count
      lastFetched.value = null;
    } catch (err) {
      console.warn('[useSubscription] Error incrementing usage:', err);
    }
  }

  return {
    // State (read-only)
    plan: readonly(plan),
    usage: readonly(usage),
    features: readonly(features),
    limits: readonly(limits),
    subscription: readonly(subscription),
    isFreeTier: readonly(isFreeTier),
    isPaidTier: readonly(isPaidTier),
    loading: readonly(usageLoading),
    error: readonly(usageError),

    // Methods
    refreshUsage,
    canCreate,
    usagePercentage,
    usageText,
    isFeatureEnabled,
    getRequiredPlan,
    getUpgradePlanForResource,
    getHighUsageResources,
    incrementUsageCounter,
  };
}
